/**
 * TriageCard — compact card rendered inside TriagePanel for a single TriageItem.
 *
 * Simpler than InsightCard: only title and description (no source user / URL).
 * Supports:
 *  - Description view with expand-on-click
 *  - Inline edit form (title + description)
 *  - Drag handle on the left for vertical reordering within the panel
 *  - Drag-to-canvas: mousedown on the right-side affordance initiates a drag
 *    gesture that Canvas handles to convert the item to a child tree node
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { TriageItem } from 'types';
import { DragHandle } from 'components/ui';

/** Height of the card in its collapsed (display) state. */
export const TRIAGE_CARD_H = 56;

type TriageCardProps = {
    item: TriageItem;
    /** Whether the card is currently expanded in edit mode. */
    isEditing: boolean;
    /** Called when the user clicks the card to enter / leave edit mode. */
    onToggleEdit: (id: string) => void;
    /** Called when the user saves edits. */
    onUpdate: (id: string, data: Partial<Omit<TriageItem, 'id' | 'type'>>) => void;
    /** Called when the user clicks the delete button. */
    onDelete: (id: string) => void;
    /** Mousedown handler from the parent for drag-to-reorder within the panel. */
    onDragHandleMouseDown: (e: React.MouseEvent, id: string) => void;
    /**
     * Called when a canvas-drag gesture begins.
     * Receives raw screen coordinates so the handler can be invoked from both
     * React synthetic events and native mousemove callbacks.
     */
    onDragToCanvasMouseDown: (clientX: number, clientY: number, id: string, isCtrlDrag?: boolean) => void;
    /**
     * Whether the Ctrl key is currently held. When true, hovering over the card
     * shows the pinch cursor to signal that a Ctrl+drag gesture is available.
     */
    isCtrlHeld: boolean;
};

export default function TriageCard({
    item,
    isEditing,
    onToggleEdit,
    onUpdate,
    onDelete,
    onDragHandleMouseDown,
    onDragToCanvasMouseDown,
    isCtrlHeld,
}: TriageCardProps) {
    const cardRef = useRef<HTMLDivElement>(null);
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    const [expandedCardHeight, setExpandedCardHeight] = useState(TRIAGE_CARD_H);

    const [form, setForm] = useState({
        title: item.title,
        description: item.description,
    });

    // Keep form in sync if the parent updates the item from outside
    useEffect(() => {
        setForm({ title: item.title, description: item.description });
    }, [item.title, item.description]);

    // Re-measure height when description is expanded
    useEffect(() => {
        if (!isDescriptionExpanded) return;
        const nextHeight = cardRef.current?.scrollHeight ?? TRIAGE_CARD_H;
        setExpandedCardHeight(Math.ceil(nextHeight));
    }, [isDescriptionExpanded, item.title, item.description]);

    // Collapse description when clicking outside
    useEffect(() => {
        if (!isDescriptionExpanded) return;
        const handleOutside = (e: MouseEvent) => {
            const target = e.target;
            if (!(target instanceof Node)) return;
            if (cardRef.current && !cardRef.current.contains(target)) {
                setIsDescriptionExpanded(false);
                setExpandedCardHeight(TRIAGE_CARD_H);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [isDescriptionExpanded]);

    const handleFormChange = useCallback(
        (field: 'title' | 'description', value: string) => {
            setForm((prev) => ({ ...prev, [field]: value }));
        },
        [],
    );

    const handleSave = useCallback(() => {
        const title = form.title.trim();
        if (!title) return;
        onUpdate(item.id, { title: form.title, description: form.description });
        onToggleEdit(item.id);
    }, [form, item.id, onUpdate, onToggleEdit]);

    const handleCancel = useCallback(() => {
        setForm({ title: item.title, description: item.description });
        onToggleEdit(item.id);
    }, [item, onToggleEdit]);

    const handleDescriptionClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            setIsDescriptionExpanded((v) => !v);
            if (!isDescriptionExpanded) {
                const nextHeight = cardRef.current?.scrollHeight ?? TRIAGE_CARD_H;
                setExpandedCardHeight(Math.ceil(nextHeight));
            } else {
                setExpandedCardHeight(TRIAGE_CARD_H);
            }
        },
        [isDescriptionExpanded],
    );

    /** URL for the pinch cursor used during the Ctrl+drag-connect gesture. */
    const pinchCursorUrl = `${process.env.PUBLIC_URL || ''}/hand-pinch-cursor.cur`;

    /** Custom grab cursor for the drag handle — mirrors InsightCard's handle exactly. */
    const grabCursor = `url(${process.env.PUBLIC_URL || ''}/hand-cursor.cur) 8 8, grab`;

    /** Movement in pixels required before a body mousedown is promoted to a canvas drag. */
    const DRAG_THRESHOLD = 8;

    /**
     * Ref that records the mousedown start position for the threshold-drag logic.
     * Set on mousedown, cleared on drag start or click (mouseup).
     */
    const bodyDragStartRef = useRef<{ x: number; y: number } | null>(null);

    /**
     * Handles mousedown on the card body:
     * - Ctrl/Meta held → immediate canvas drag (no threshold, explicit intent signalled).
     * - Plain drag (body moved ≥ DRAG_THRESHOLD px) → canvas drag.
     * - Click (released before threshold) → open / close the edit form.
     *
     * This gives users two ergonomic ways to drag without needing to find the ↗ handle:
     * hold Ctrl for a deliberate connect gesture, or just press and drag naturally.
     */
    const handleBodyMouseDown = useCallback(
        (e: React.MouseEvent) => {
            // Ctrl/Meta: immediate canvas drag — no movement threshold needed.
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                e.stopPropagation();
                onDragToCanvasMouseDown(e.clientX, e.clientY, item.id, true);
                return;
            }

            // Plain mousedown: watch for sufficient movement before committing to a drag.
            e.preventDefault();
            bodyDragStartRef.current = { x: e.clientX, y: e.clientY };

            const onMove = (mv: MouseEvent) => {
                const start = bodyDragStartRef.current;
                if (!start) return;
                const dist = Math.hypot(mv.clientX - start.x, mv.clientY - start.y);
                if (dist >= DRAG_THRESHOLD) {
                    // Movement exceeded threshold — promote to canvas drag.
                    bodyDragStartRef.current = null;
                    cleanup();
                    onDragToCanvasMouseDown(mv.clientX, mv.clientY, item.id);
                }
            };

            const onUp = () => {
                if (bodyDragStartRef.current) {
                    // Released without significant movement — treat as a click to edit.
                    bodyDragStartRef.current = null;
                    onToggleEdit(item.id);
                }
                cleanup();
            };

            const cleanup = () => {
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onUp);
            };

            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
        },
        [item.id, onDragToCanvasMouseDown, onToggleEdit],
    );

    if (isEditing) {
        return (
            <div className='triage-card triage-card--editing' ref={cardRef}>
                <label className='triage-field-label'>Title *</label>
                <input
                    className='triage-field-input'
                    value={form.title}
                    onChange={(e) => handleFormChange('title', e.target.value)}
                    placeholder='Triage item title'
                    autoFocus
                    maxLength={200}
                />

                <label className='triage-field-label'>Description</label>
                <textarea
                    className='triage-field-textarea'
                    value={form.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    placeholder='Describe this item…'
                    rows={3}
                    maxLength={500}
                />
                <span className='triage-field-char-count'>{(form.description || '').length} / 500</span>

                <div className='triage-form-actions'>
                    <button
                        className='triage-btn triage-btn--save'
                        onClick={handleSave}
                        disabled={!form.title.trim()}
                    >
                        Save
                    </button>
                    <button className='triage-btn triage-btn--cancel' onClick={handleCancel}>
                        Cancel
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            ref={cardRef}
            className={`triage-card ${isCtrlHeld ? 'triage-card--ctrl-ready' : ''}`}
            style={{
                // Always set the height var (matches InsightCard's pattern) so the collapsed
                // and expanded states are driven the exact same way as insight cards.
                '--card-height': isDescriptionExpanded ? `${expandedCardHeight}px` : `${TRIAGE_CARD_H}px`,
                // Show the pinch cursor when Ctrl is held to signal the drag-connect gesture is available
                ...(isCtrlHeld ? { cursor: `url(${pinchCursorUrl}) 16 16, crosshair` } : undefined),
            } as React.CSSProperties}
        >
            {/* Left drag handle — for reordering within the panel; mirrors InsightCard's handle */}
            <DragHandle
                className='triage-card__drag-handle'
                title='Drag to reorder'
                style={{ cursor: grabCursor }}
                onMouseDown={(e) => {
                    // Don't start a reorder drag if Ctrl is held — let the body handler take it.
                    if (e.ctrlKey || e.metaKey) return;
                    e.stopPropagation();
                    onDragHandleMouseDown(e, item.id);
                }}
            />

            {/* Main content — mousedown here starts a canvas drag (with movement threshold) */}
            <div className='triage-card__body' onMouseDown={handleBodyMouseDown}>
                <div className='triage-card__title-row'>
                    <p className='triage-card__title'>{item.title}</p>
                    {item.sourceNodeType && (
                        <span className={`triage-card__source-tag triage-card__source-tag--${item.sourceNodeType}`}>
                            from {item.sourceNodeType}
                        </span>
                    )}
                </div>
                <div className='triage-card__meta'>
                    {item.description && (
                        <div
                            className={`triage-card__description ${isDescriptionExpanded ? 'triage-card__description--expanded' : ''}`}
                            onClick={handleDescriptionClick}
                        >
                            {item.description}
                        </div>
                    )}
                </div>
            </div>

            {/* Right controls — delete button sits in the upper-right corner, unchanged */}
            <div className='triage-card__controls'>
                {/* Delete button */}
                <button
                    className='triage-card__btn triage-card__btn--delete'
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(item.id);
                    }}
                    title='Delete triage item'
                    aria-label='Delete triage item'
                >
                    ×
                </button>

                {/* Drag-to-canvas handle — explicit handle for users who prefer it over body drag */}
                <div
                    className='triage-card__canvas-drag-handle'
                    onMouseDown={(e) => {
                        e.stopPropagation();
                        onDragToCanvasMouseDown(e.clientX, e.clientY, item.id);
                    }}
                    title='Drag onto a tree node to place'
                    aria-label='Drag to canvas'
                >
                    ↗
                </div>
            </div>
        </div>
    );
}
